import { Group, Stack } from "@mantine/core"
import { Loader, Select } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { useFindMaterialTypesByOperationChain } from "~/gen/api"
import { EditableCellWithTextInput } from "~/pages/Admin/Translations/columns.tsx"
import { useUpdateMaterialMutation } from "~/pages/Brand/Products/Details/hooks.ts"
import { formatMaterialTypeSelectOptions } from "~/pages/Brand/Products/Details/utils"

type Props = {
  material: LibraryResponses.EnrichedMaterial
}

export function MaterialPropertySelection({ material }: Props) {
  const { data: materialTypeList, isLoading } =
    useFindMaterialTypesByOperationChain({
      operationChainName: material.type.materialOpChain,
    })

  const {
    i18n: { language },
  } = useTranslation()

  const { mutate: updateMaterial } = useUpdateMaterialMutation()

  const selectOptions = useMemo(() => {
    return formatMaterialTypeSelectOptions(
      materialTypeList?.data.materialTypes || [],
      material.type.id,
      language.toUpperCase()
    )
  }, [material, materialTypeList, language])

  if (isLoading) {
    return <Loader fullPage />
  } else {
    return (
      <Stack gap="xs" align="flex-start">
        <Group justify="space-between" w="100%" wrap="nowrap">
          <Select
            value={material.type.id.toString()}
            data={selectOptions}
            onChange={(newValue) => {
              updateMaterial({
                id: material.id,
                typeId: materialTypeList?.data?.materialTypes?.find(
                  (materialType) => materialType.id === Number(newValue)
                )?.id,
              })
            }}
            searchable
          />
          <EditableCellWithTextInput
            key={material.id}
            defaultValue={material.proportion.toString()}
            onSave={(newValue: string) =>
              updateMaterial({
                id: material.id,
                proportion: parseFloat(newValue),
              })
            }
          />
        </Group>
      </Stack>
    )
  }
}
